import { Component, effect, input, output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LightboxData } from '../business-profile.interface';
import { ImageMenuClick } from './media-tab-gallery/image-menu-click';
import { ClientImage, MenuOption } from './media-tab.interface';
import { MediaTabService } from './media-tab.service';

@Component({
  selector: 'business-media-tab',
  templateUrl: './media-tab.component.html',
  styleUrls: ['./media-tab.component.scss'],
})
export class MediaTabComponent {
  accountGroupId = input.required<string>();
  editDisabled = input<boolean>(false);
  imageClicked = output<LightboxData>();

  protected readonly images$: Observable<ClientImage[]>;

  constructor(
    public dialog: MatDialog,
    public tabService: MediaTabService,
  ) {
    this.images$ = this.tabService.imagesSubject$$.pipe(map((arr) => (arr.length > 0 ? arr : null)));
    effect(() => {
      this.tabService.setAccountGroupId(this.accountGroupId());
    });
  }

  public get galleryInfo$(): Observable<any> {
    return combineLatest([
      this.tabService.totalImages$$,
      this.tabService.hasMore,
      this.tabService.loading$$,
      this.tabService.pageFetchError$$,
    ]).pipe(
      map(([totalImages, hasMore, loading, pageFetchError]) => ({ totalImages, hasMore, loading, pageFetchError })),
    );
  }

  loadMore(): void {
    this.tabService.addClientImages();
  }

  openImageUploadDialog(): void {
    this.tabService.openImageUploadDialog(this.dialog);
  }

  async openLightbox(clickedImage: ClientImage) {
    const imageUrls: string[] = await firstValueFrom(
      this.images$.pipe(map((images) => images.map((image) => image.url))),
    );
    this.imageClicked.emit({ imageUrls: imageUrls, index: imageUrls.indexOf(clickedImage.url) });
  }

  async imageMenuClicked(clickedOption: ImageMenuClick): Promise<void> {
    const getMenuKey = (key) => `FRONTEND.BUSINESSES.MEDIA_TAB.IMAGE_MENU.${key}`;
    if (clickedOption.option === MenuOption.Logo) {
      const dialogRef = this.dialog.open(ConfirmationModalComponent);
      dialogRef.componentInstance.title = getMenuKey('SET_LOGO_QUESTION');
      dialogRef.componentInstance.body = getMenuKey('SET_LOGO_BODY');
      dialogRef.componentInstance.confirmButton = getMenuKey('SET_LOGO');
      this.tabService.markImageAsLogo(clickedOption.image.url, dialogRef);
    } else if (clickedOption.option === MenuOption.Primary) {
      const dialogRef = this.dialog.open(ConfirmationModalComponent);
      dialogRef.componentInstance.title = getMenuKey('SET_PRIMARY_PHOTO');
      dialogRef.componentInstance.body = getMenuKey('SET_PRIMARY_BODY');
      dialogRef.componentInstance.confirmButton = getMenuKey('SET_PRIMARY');
      this.tabService.markImageAsPrimary(clickedOption.image.url, dialogRef);
    } else if (clickedOption.option === MenuOption.Delete) {
      const dialogRef = this.dialog.open(ConfirmationModalComponent);
      dialogRef.componentInstance.title = getMenuKey('DELETE_PHOTO_QUESTION');
      dialogRef.componentInstance.body = getMenuKey('DELETE_PHOTO_BODY');
      dialogRef.componentInstance.confirmButton = getMenuKey('DELETE_PHOTO');
      this.tabService.deleteImage(clickedOption.image.url, dialogRef);
    }
  }
}

@Component({
  selector: 'business-confirmation-modal',
  template: `
    <mat-dialog-content class="mat-dialog-content">
      <h2 mat-dialog-title class="mat-dialog-title">{{ title | translate }}</h2>
      <p>{{ body | translate }}</p>
    </mat-dialog-content>
    <mat-dialog-actions class="mat-dialog-actions">
      <button mat-button color="primary" (click)="onCancel()" type="button" mat-dialog-close>
        {{ 'FRONTEND.BUSINESSES.CANCEL' | translate }}
      </button>
      <button mat-raised-button color="primary" (click)="onConfirm()" type="button" mat-dialog-close>
        {{ confirmButton | translate }}
      </button>
    </mat-dialog-actions>
  `,
})
export class ConfirmationModalComponent {
  // Translation key for modal title
  public title: string;
  // Translation key for modal body
  public body: string;
  // Translation key for modal confirm button
  public confirmButton: string;
  constructor(private dialogRef: MatDialogRef<ConfirmationModalComponent>) {}

  onConfirm(): void {
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
